// src/components/StockTag.jsx
//
// The hero object. A workshop stock tag, hung on a string, that cycles
// through the newest units on the floor. It plays the same role as the data
// plate on Cold Storage: typographic and hairline, real data rather than
// decoration, and it holds up at any size.
//
// Every value on it comes from the listing, so it never claims anything the
// catalogue does not.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowRight, Package } from "lucide-react";
import { Tilt } from "./Effects";
import { makeModel, mediaUrl, priceLabel } from "../lib/format";

const CYCLE_MS = 4800;

export default function StockTag({ items }) {
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = items?.length || 0;

  useEffect(() => {
    if (reduce || paused || n < 2) return;
    const t = setInterval(() => setI((v) => (v + 1) % n), CYCLE_MS);
    return () => clearInterval(t);
  }, [reduce, paused, n]);

  // A new list (first load) starts from the newest again
  useEffect(() => setI(0), [n]);

  const item = n ? items[i % n] : null;

  return (
    <div
      className="relative mx-auto w-full max-w-[400px] pt-14"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* string, from above the frame down to the punched hole */}
      <svg
        aria-hidden="true"
        viewBox="0 0 120 70"
        className="pointer-events-none absolute left-1/2 top-0 h-[70px] w-[120px] -translate-x-1/2"
      >
        <path
          d="M60 0 C 52 22, 68 40, 60 66"
          fill="none"
          stroke="rgb(var(--bk-subtle))"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </svg>

      <div className="bk-swing origin-top">
        <Tilt max={5}>
          <div className="bk-plate relative overflow-hidden rounded-2xl border border-line shadow-[0_28px_60px_-28px_rgba(4,12,20,0.6)]">
            <div className="bk-sheen pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-[linear-gradient(100deg,transparent,rgb(var(--bk-surface)/0.8),transparent)]" />

            {/* punched hole with its reinforcing ring */}
            <span className="absolute left-1/2 top-4 h-5 w-5 -translate-x-1/2 rounded-full border-2 border-line bg-bg shadow-[inset_0_1px_3px_rgba(0,0,0,.35)]" />

            <div className="relative px-6 pb-6 pt-12 sm:px-8">
              <div className="flex items-start justify-between border-b border-line pb-4">
                <div>
                  <p className="font-mono text-[8.5px] uppercase tracking-[0.24em] text-subtle">
                    Brooke Refrigeration
                  </p>
                  <p className="mt-1 font-arch text-sm font-extrabold uppercase tracking-tight text-ink">
                    Requip stock tag
                  </p>
                </div>
                <span className="flex items-center font-mono text-[8.5px] uppercase tracking-[0.18em] text-subtle">
                  <i className="bk-dot mr-1.5 h-[5px] w-[5px] rounded-full bg-[#41D98A] shadow-[0_0_8px_#41D98A]" />
                  {n ? "Available" : "Restocking"}
                </span>
              </div>

              <div className="relative min-h-[300px]">
                <AnimatePresence mode="wait" initial={false}>
                  {item ? (
                    <TagBody key={item.id} item={item} />
                  ) : (
                    <Waiting key="waiting" loading={items === null} />
                  )}
                </AnimatePresence>
              </div>

              {n > 1 && (
                <div className="mt-4 flex items-center justify-between border-t border-line pt-4">
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-subtle">
                    Tag {String(i + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
                  </span>
                  <div className="flex gap-1.5">
                    {items.map((it, idx) => (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => setI(idx)}
                        aria-label={`Show ${it.title}`}
                        className={`h-1.5 rounded-full transition-all ${
                          idx === i ? "w-5 bg-brooke-red" : "w-1.5 bg-line hover:bg-subtle"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Tilt>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function TagBody({ item }) {
  const img = item.images?.[0];
  const mm = makeModel(item);
  const listed = new Date(item.created_at).toLocaleDateString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return (
    <motion.div
      initial={{ opacity: 0, x: 18 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -18 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link to={`/${item.slug}`} className="group block pt-5">
        <div className="relative aspect-[16/10] overflow-hidden rounded-lg border border-line bg-surface2">
          {img ? (
            <img
              src={mediaUrl(img.thumb || img.path)}
              alt={item.title}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
            />
          ) : (
            <div className="grid h-full place-items-center text-subtle">
              <Package size={28} strokeWidth={1.3} />
            </div>
          )}

          {/* rubber stamp, pressed on each time a new tag comes up */}
          <motion.span
            initial={{ opacity: 0, scale: 1.5, rotate: -18 }}
            animate={{ opacity: 0.92, scale: 1, rotate: -9 }}
            transition={{ delay: 0.25, type: "spring", stiffness: 420, damping: 18 }}
            className="absolute right-3 top-3 rounded border-2 border-brooke-red bg-bg/70 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-brooke-red backdrop-blur-sm"
          >
            In stock
          </motion.span>
        </div>

        <p className="mt-4 line-clamp-2 font-arch text-lg font-extrabold leading-snug tracking-tight text-ink transition group-hover:text-brooke-red">
          {item.title}
        </p>

        <dl className="mt-3 border-t border-line">
          <Row label="Make / model" value={mm || "-"} />
          <Row label="Category" value={item.category || "-"} />
          <Row label="Listed" value={listed} />
        </dl>

        <div className="mt-4 flex items-end justify-between">
          <span className="font-mono text-[26px] font-bold leading-none tabular-nums tracking-tight text-led">
            {priceLabel(item)}
          </span>
          <span className="flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em] text-subtle transition group-hover:text-brooke-red">
            View unit
            <ArrowRight size={13} />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-6 border-b border-line py-2.5">
      <dt className="font-mono text-[9px] uppercase tracking-[0.18em] text-subtle">{label}</dt>
      <dd className="truncate text-right font-mono text-[11.5px] tracking-[0.04em] text-muted">
        {value}
      </dd>
    </div>
  );
}

function Waiting({ loading }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex min-h-[300px] flex-col justify-center pt-5"
    >
      {loading ? (
        <div className="space-y-3">
          <div className="aspect-[16/10] animate-pulse rounded-lg bg-surface2" />
          <div className="h-5 w-3/4 animate-pulse rounded bg-surface2" />
          <div className="h-3 w-full animate-pulse rounded bg-surface2" />
        </div>
      ) : (
        <>
          <p className="font-arch text-xl font-extrabold text-ink">Next unit coming in</p>
          <p className="mt-2 text-[14px] leading-relaxed text-muted">
            The floor is clear right now. Tell us what you are after and the
            office will let you know.
          </p>
          <a
            href="#enquire"
            className="mt-5 font-mono text-[10px] uppercase tracking-[0.16em] text-brooke-red"
          >
            Leave a request
          </a>
        </>
      )}
    </motion.div>
  );
}